// @ts-nocheck
import * as React from 'react';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';

interface SearchableSelectProps<T extends string | number> {
	options?: Array<{
		value: T;
		label: string;
		data?: unknown;
		render?: () => React.ReactNode;
	}>;
	value: T;
	onChange?: (
		value: T,
		option?: { value: T; label: string; data?: unknown }
	) => void;
	onValueChange?: (value: T) => void;
	placeholder?: string;
	maintainFocus?: boolean;
	items?: unknown[];
	valueField?: string;
	renderItem?: (item: unknown) => React.ReactNode;
	searchTerm?: string;
	setSearchTerm?: (term: string) => void;
}

export function SearchableSelect<T extends string | number>({
	options = [],
	value,
	onChange,
	onValueChange,
	placeholder = 'Select...',
	items,
	valueField,
	renderItem,
}: SearchableSelectProps<T>) {
	const [open, setOpen] = React.useState(false);
	const [searchTermState, setSearchTermState] = React.useState('');
	const inputRef = React.useRef<HTMLInputElement>(null);

	const finalOptions = items
		? items.map((item: unknown) => ({
				value: valueField ? (item as Record<string, unknown>)[valueField] : item,
				label: (item as { label?: string }).label || item?.toString() || '',
				data: item,
				render: () => (renderItem ? renderItem(item) : item?.toString() || ''),
			}))
		: options;

	const handleChange = (
		newValue: T,
		option?: { value: T; label: string; data?: unknown }
	) => {
		if (onChange) {
			onChange(newValue, option);
		} else if (onValueChange) {
			onValueChange(newValue);
		}
	};

	const filteredOptions = React.useMemo(() => {
		if (!finalOptions) return [];

		return finalOptions.filter((option) => {
					// If it's a profile search (has data property with profileTitle/email)
		const data = option.data as { profileTitle?: string; email?: string };
		if (data?.profileTitle || data?.email) {
			const profile = data;
				const searchLower = searchTermState?.toLowerCase() || '';
				const profileTitle = profile.profileTitle?.toLowerCase() || '';
				const email = profile.email?.toLowerCase() || '';

				return (
					profileTitle.includes(searchLower) ||
					email.includes(searchLower)
				);
			}

			// Regular option search (timezone selector etc)
			const searchLower = searchTermState?.toLowerCase() || '';
			const label = option.label?.toLowerCase() || '';

			return label.includes(searchLower);
		});
	}, [finalOptions, searchTermState]);

	const selectedOption = finalOptions.find(
		(option) => option.value === value
	);

	React.useEffect(() => {
		if (open) {
			setTimeout(() => {
				inputRef.current?.focus();
			}, 0);
		}
	}, [open, searchTermState]);

	return (
		<Select
			open={open}
			onOpenChange={setOpen}
			value={String(value)}
			onValueChange={(newValue) => {
				const option = finalOptions.find(
					(opt) => String(opt.value) === newValue
				);
				handleChange(newValue as T, option);
				setOpen(false);
				setSearchTermState('');
			}}
		>
			<SelectTrigger className='w-full'>
				<SelectValue placeholder={placeholder}>
					<span className='block truncate'>
						{selectedOption?.render
							? selectedOption.render()
							: selectedOption?.label || placeholder}
					</span>
				</SelectValue>
			</SelectTrigger>
			<SelectContent>
				<div className='px-2 py-2'>
					<Input
						ref={inputRef}
						placeholder='Search...'
						value={searchTermState}
						onChange={(e) => {
							e.preventDefault();
							setSearchTermState(e.target.value);
						}}
						onKeyDown={(e) => e.stopPropagation()}
						className='mb-2'
					/>
				</div>
				<div className='max-h-[200px] overflow-y-auto'>
					{filteredOptions.length ? (
						filteredOptions.map((option) => (
							<SelectItem
								key={String(option.value)}
								value={String(option.value)}
								className='cursor-pointer'
							>
								{option.render
									? option.render()
									: option.label || String(option.value)}
							</SelectItem>
						))
					) : (
						<div className='px-3 py-2 text-sm text-muted-foreground'>
							No results found
						</div>
					)}
				</div>
			</SelectContent>
		</Select>
	);
}
